import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["playButton", "progressBar"]
  static outlets = ["video-player"]
  static values = {
    active: Boolean,
    playing: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    sectionId: Number,
    videoSource: String,
    poster: String,
    timestamp: Number,
    duration: Number, // Duration of this section in seconds
    autoAdvance: { type: Boolean, default: true }, // Whether to auto-advance to next item
    currentPlaybackTime: Number, // Track current playback position within this section
    openUrl: String,
    bookmarkKey: String,
    bookmarkableType: String,
    bookmarkableId: Number,
    bookmarkAuthPath: String,
    bookmarkCompletionPath: String,
    bookmarkTitle: String,
    chaptersUrl: String,
    captionsUrl: String
  }

  connect() {
    // Initialize current playback time to the beginning of the section
    this.currentPlaybackTimeValue = this.timestampValue

    // Listen for video time updates to sync progress bar
    this.boundUpdateProgress = this.updateProgress.bind(this)
    document.addEventListener("video-player:timeupdate", this.boundUpdateProgress)

    // Listen for video player events
    this.boundHandlePlay = this.handlePlay.bind(this)
    this.boundHandlePause = this.handlePause.bind(this)
    this.boundHandleSeeked = this.handleSeeked.bind(this)
    this.boundHandleEnded = this.handleEnded.bind(this)
    document.addEventListener("video-player:play", this.boundHandlePlay)
    document.addEventListener("video-player:pause", this.boundHandlePause)
    document.addEventListener("video-player:seeked", this.boundHandleSeeked)
    document.addEventListener("video-player:ended", this.boundHandleEnded)

    // Initialize progress bar
    this.resetProgress()
  }

  disconnect() {
    if (this.boundUpdateProgress) {
      document.removeEventListener("video-player:timeupdate", this.boundUpdateProgress)
    }
    if (this.boundHandlePlay) {
      document.removeEventListener("video-player:play", this.boundHandlePlay)
    }
    if (this.boundHandlePause) {
      document.removeEventListener("video-player:pause", this.boundHandlePause)
    }
    if (this.boundHandleSeeked) {
      document.removeEventListener("video-player:seeked", this.boundHandleSeeked)
    }
    if (this.boundHandleEnded) {
      document.removeEventListener("video-player:ended", this.boundHandleEnded)
    }
  }

  toggle(event) {
    if (this.interactiveElementClicked(event)) return
    if (event?.type === "keydown") event.preventDefault()

    if (this.activeValue && this.hasVideoPlayerOutlet) {
      // I'm active toggle the play pause on the video player
      this.videoPlayerOutlet.togglePlayPause()
    } else {
      this.play()
    }
  }

  interactiveElementClicked(event) {
    return event?.target?.closest("a,button,form,label,input,select,textarea")
  }

  bookmarkControl() {
    return {
      dataset: {
        bookmarkKey: this.bookmarkKeyValue,
        bookmarkableType: this.bookmarkableTypeValue,
        bookmarkableId: String(this.bookmarkableIdValue),
        bookmarkReturnTo: window.location.pathname + window.location.search,
        bookmarkAuthPath: this.bookmarkAuthPathValue,
        bookmarkCompletionPath: this.bookmarkCompletionPathValue
      },
      title: this.bookmarkTitleValue,
      getAttribute: () => null
    }
  }

  play() {
    if (!this.hasVideoPlayerOutlet) return

    // Set loading state immediately
    this.loadingValue = true
    this.activeValue = true

    // Always start from the beginning of the section to ensure clean playback
    // Reset current playback time to avoid resume issues
    this.currentPlaybackTimeValue = this.timestampValue
    const startTime = this.timestampValue

    this.videoPlayerOutlet.updatePlaylistControls?.({
      openUrl: this.openUrlValue,
      bookmarkControl: this.bookmarkControl(),
      poster: this.posterValue
    })

    // Set flag to ignore ended events briefly (Vidstack race condition protection)
    this.justStartedPlaying = true
    setTimeout(() => {
      this.justStartedPlaying = false
    }, 2000) // 2 second protection window

    // Calculate clip end time
    const clipStart = this.timestampValue
    const clipEnd = this.timestampValue + this.durationValue

    // First pause and reset the player to ensure clean state switching
    if (this.hasVideoPlayerOutlet) {
      this.videoPlayerOutlet.pauseVideo()
    }

    // Small delay to ensure pause completes before switching
    setTimeout(() => {
      this.videoPlayerOutlet
        .seekAndPlay(startTime, this.videoSourceValue, clipStart, clipEnd, this.chaptersUrlValue, this.captionsUrlValue)
        .then(() => {
          // Force play after a short delay to ensure the seek has completed
        })
        .catch((error) => {
          console.error("Error jumping to timestamp or playing video:", error)
          // Reset flags on error
          this.loadingValue = false
          this.justStartedPlaying = false
        })
    }, 50)

    this.dispatch("activated", {
      detail: {
        sectionId: this.sectionIdValue,
        videoSource: this.videoSourceValue,
        timestamp: this.timestampValue,
        element: this.element
      }
    })
  }

  pause() {
    this.playingValue = false
    this.loadingValue = false
    if (this.hasVideoPlayerOutlet) {
      this.videoPlayerOutlet.pauseVideo()
    }
    this.dispatch("deactivated", {
      detail: { videoSource: this.videoSourceValue, element: this.element }
    })
  }

  // Handle player events
  handlePlay() {
    // Only respond if this is the active item
    if (this.activeValue) {
      this.loadingValue = false
      this.playingValue = true

      // Notify playlist controller that this item started playing
      this.dispatch("playing", {
        detail: { element: this.element },
        bubbles: true
      })
    }
  }

  handlePause() {
    // Only respond if this is the active item
    if (this.activeValue) {
      this.playingValue = false
    }
  }

  handleSeeked() {
    // Only respond if this is the active item and we're in loading state
    if (this.activeValue && this.loadingValue) {
      // The seek completed, so we're no longer loading
      this.loadingValue = false
      this.playingValue = true

    }
  }

  handleEnded() {
    // Ignore ended events if we just started playing (race condition protection)
    if (this.justStartedPlaying) {
      return
    }

    // Only respond if this is the active item
    if (this.activeValue && this.autoAdvanceValue) {
      // Notify playlist controller that this item ended
      this.dispatch("ended", {
        detail: { element: this.element },
        bubbles: true
      })
    }
  }

  // Deactivate this item when another item becomes active
  deactivate(event) {
    if (event.detail.element !== this.element) {
      this.activeValue = false
      this.playingValue = false
      this.loadingValue = false
      this.currentPlaybackTimeValue = this.timestampValue // Reset to beginning
      this.resetProgress()
    }
  }

  updateProgress(event) {
    // Only update if this item is active, playing, and not loading
    // Also check that the current video source matches this item's source to prevent cross-contamination
    if (!this.activeValue || !this.playingValue || this.loadingValue) return

    // Verify this update is for our video source
    if (event.detail.videoSource && event.detail.videoSource !== this.videoSourceValue) return

    this.progressBarTarget.value = event.detail.percent
  }
  resetProgress() {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.value = 0
      this.progressBarTarget.setAttribute("value", "0") // Ensure it's determinate
    }
  }

  // Reset playback to the beginning of the section
  resetToBeginning() {
    this.currentPlaybackTimeValue = this.timestampValue
    this.resetProgress()
  }

  // Value change callbacks
  activeValueChanged(value) {
    if (!value) {
      this.loadingValue = false
      this.currentPlaybackTimeValue = this.timestampValue // Reset to beginning when deactivated
      this.resetProgress()
    }
  }

  playingValueChanged() {
    // No visual animation - just rely on the moving progress bar
  }

  loadingValueChanged() {
    // Update visual state based on loading
    this.updateProgressBarState()
  }

  currentPlaybackTimeValueChanged() {
    // This method can be used for future enhancements if needed
  }

  updateProgressBarState() {
    if (!this.hasProgressBarTarget) return

    if (this.loadingValue) {
      // Show pulsating animation while loading
      this.progressBarTarget.classList.add("animate-pulse")
      this.progressBarTarget.removeAttribute("value") // Make it indeterminate
    } else {
      // Remove pulsating animation when not loading
      this.progressBarTarget.classList.remove("animate-pulse")
      // Ensure it's determinate - the actual progress value will be set by updateProgress
      if (!this.progressBarTarget.hasAttribute("value")) {
        this.progressBarTarget.setAttribute("value", "0")
        this.progressBarTarget.value = 0
      }
    }
  }
}
